import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Maximize2, LocateFixed, SlidersHorizontal, Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DraggablePanel from '@/components/shared/DraggablePanel';
import { useReplayPlayerLayout } from '@/hooks/useReplayPlayerLayout';
import useReplayTrackWidth from '@/hooks/useReplayTrackWidth';
import ReplayPlayerControls from './ReplayPlayerControls';
import ReplayEventTrack from './ReplayEventTrack';
import ReplayLayerFilters, { REPLAY_LAYERS } from './ReplayLayerFilters';
import ReplayTimeline from './ReplayTimeline';

const LAYERS_KEY = 'trackizy_livemap_replay_layers';

function readLayers() {
  const base = Object.fromEntries(REPLAY_LAYERS.map((l) => [l.key, l.defaultOn]));
  try { return { ...base, ...JSON.parse(localStorage.getItem(LAYERS_KEY) || '{}') }; } catch { return base; }
}

/**
 * Floating, draggable, resizable trip player. Self-contained: header +
 * playback controls + icon-badge track + filter chips + the trip timeline.
 * Portaled over the map. Replaces the old bottom-strip ReplayForensicsPanel.
 */
export default function ReplayTripPlayer({
  deviceName, positions, progress, playing, speed, currentPosition,
  onToggle, onSeek, onSpeedChange, onClose, onFitRoute, follow, onToggleFollow,
  events, activeId, onSeekToEvent,
}) {
  const { t } = useTranslation();
  const { x, y, width, height, minW, minH, setPos, setSize } = useReplayPlayerLayout();

  const { width: trackWidth, setWidth: setTrackWidth, bounds: trackBounds } = useReplayTrackWidth();
  const [trackMenuOpen, setTrackMenuOpen] = useState(false);
  const trackMenuRef = useRef(null);
  useEffect(() => {
    if (!trackMenuOpen) return undefined;
    const onDown = (e) => { if (trackMenuRef.current && !trackMenuRef.current.contains(e.target)) setTrackMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [trackMenuOpen]);

  const [layers, setLayers] = useState(readLayers);
  const toggleLayer = useCallback((key) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(LAYERS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const visibleEvents = useMemo(() => events.filter((e) => layers[e.kind]), [events, layers]);

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const glass = isDark
    ? { background: 'rgba(15,23,42,0.82)', border: '1px solid rgba(255,255,255,0.08)' }
    : { background: 'rgba(255,255,255,0.80)', border: '1px solid rgba(255,255,255,0.55)' };

  const totalMin = positions && positions.length >= 2
    ? dayjs(positions[positions.length - 1].fixTime).diff(dayjs(positions[0].fixTime), 'minute') : 0;
  const tripSummary = t('replayPlayer.tripSummary', '{{h}}h {{m}}m · {{count}} events', {
    h: Math.floor(totalMin / 60), m: totalMin % 60, count: events.length,
  });

  const card = (
    <DraggablePanel
      x={x} y={y} width={width} height={height}
      minW={minW} minH={minH}
      onMove={setPos} onResize={setSize}
      gripLabel={t('replayPlayer.drag', 'Move replay player')}
      className="z-40 rounded-[18px] shadow-2xl"
      style={{ ...glass, backdropFilter: 'blur(40px) saturate(180%)', WebkitBackdropFilter: 'blur(40px) saturate(180%)' }}
    >
      {/* header */}
      <div className="flex items-center gap-2 px-3.5 pb-2 flex-shrink-0">
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-slate-900 dark:text-slate-100 tracking-tight truncate">
            <bdi>{deviceName}</bdi>
          </span>
          <span className="block text-[11px] font-medium text-slate-400 truncate">{tripSummary}</span>
        </span>
        {onToggleFollow && (
          <button
            type="button"
            onClick={onToggleFollow}
            aria-pressed={!!follow}
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
              follow ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-600 hover:bg-black/5'
            }`}
            aria-label={t('replayPlayer.follow', 'Follow vehicle')}
            title={t('replayPlayer.follow', 'Follow vehicle')}
          >
            <LocateFixed size={14} />
          </button>
        )}
        {onFitRoute && (
          <button
            type="button"
            onClick={onFitRoute}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-black/5 flex-shrink-0"
            aria-label={t('miniReplay.fitRoute', 'Fit route')}
            title={t('miniReplay.fitRoute', 'Fit route')}
          >
            <Maximize2 size={14} />
          </button>
        )}
        <span ref={trackMenuRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setTrackMenuOpen((v) => !v)}
            aria-pressed={trackMenuOpen}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              trackMenuOpen ? 'bg-black/10 text-slate-700 dark:text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-black/5'
            }`}
            aria-label={t('replayPlayer.trackStyle', 'Track style')}
            title={t('replayPlayer.trackStyle', 'Track style')}
          >
            <SlidersHorizontal size={14} />
          </button>
          {trackMenuOpen && (
            <div
              className="absolute end-0 top-full mt-1.5 z-50 rounded-xl p-3 shadow-2xl"
              style={{ ...glass, backdropFilter: 'blur(40px) saturate(180%)', WebkitBackdropFilter: 'blur(40px) saturate(180%)', minWidth: 168 }}
            >
              <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-2">
                {t('replayPlayer.trackWidth', 'Track width')}
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setTrackWidth(trackWidth - 1)}
                  disabled={trackWidth <= trackBounds.min}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label={t('replayPlayer.thinner', 'Thinner')}
                >
                  <Minus size={14} />
                </button>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-100 tabular-nums">
                  <bdi>{trackWidth} px</bdi>
                </span>
                <button
                  type="button"
                  onClick={() => setTrackWidth(trackWidth + 1)}
                  disabled={trackWidth >= trackBounds.max}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label={t('replayPlayer.thicker', 'Thicker')}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          )}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-black/5 flex-shrink-0"
          aria-label={t('miniReplay.exitReplay', 'Exit replay')}
        >
          <X size={16} />
        </button>
      </div>

      <ReplayPlayerControls
        positions={positions} progress={progress} playing={playing} speed={speed}
        currentPosition={currentPosition} onToggle={onToggle} onSeek={onSeek} onSpeedChange={onSpeedChange}
      />

      <div className="px-3.5 pb-1 flex-shrink-0">
        <ReplayEventTrack events={visibleEvents} activeId={activeId} onSelect={onSeekToEvent} />
      </div>

      <div className="px-3 pb-2 flex-shrink-0">
        <ReplayLayerFilters enabled={layers} onToggle={toggleLayer} />
      </div>

      <ReplayTimeline events={visibleEvents} activeId={activeId} onSelect={onSeekToEvent} />
    </DraggablePanel>
  );

  return createPortal(card, document.body);
}
